import * as React from 'react'
import loadable from '@loadable/component'

const ChevronDown = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronDown'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
import { SafeHtml, Button } from '@thg-commerce/gravity-elements'
import {
  styled,
  Text,
  spacing,
  css,
  gradient,
  withTheme,
  opacity,
} from '@thg-commerce/gravity-theme'

import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'

import { CombinedThemeInterface } from '../'

const DEFAULT_LINE_HEIGHT = '24px'
const NUM_OF_LINES_TO_SHOW = 3

export interface ReadMoreProps {
  readMoreText: string
  readLessText: string
  content: string
  horizontalAlignment?: string
}

const Container = styled.div`
  width: 100%;
`

const Content = styled(SafeHtml)<{
  open: boolean
  showReadMore: boolean
  maxHeight: string
}>`
  ${(props) =>
    props.showReadMore &&
    css`
      ${props.open && `margin-bottom: ${spacing(2)};`}
      overflow: hidden;
      max-height: ${props.maxHeight};
      transition: max-height 0.5s ease;
    `}
`

const StyledChevronDown = styled(ChevronDown)<{
  open: boolean
}>`
  transform: ${(props) => (props.open ? `rotate(180deg)` : `rotate(0deg)`)};
  transition: transform 0.5s ease;
`

const bottomBlur = css<{
  lineHeight: string
}>`
  flex-wrap: wrap;
  position: relative;
  bottom: ${(props) => props.lineHeight};
  margin-bottom: -${(props) => props.lineHeight};

  &::before {
    content: '';
    display: block;
    height: ${(props) => props.lineHeight};
    width: 100%;
    background: ${(props) =>
      gradient('0deg', [
        { color: opacity(props.theme.colors.palette.greys.white, 1), stop: 0 },
        {
          color: opacity(props.theme.colors.palette.greys.white, 0),
          stop: 100,
        },
      ])};
  }
`

const ButtonIconContainer = styled.div<{
  open: boolean
  lineHeight: string
  horizontalAlignment?: string
}>`
  display: flex;
  align-items: center;
  width: 100%;

  ${(props) =>
    props.horizontalAlignment === HorizontalAlignment.CENTER &&
    `justify-content: center;`}

  ${(props) => !props.open && bottomBlur}
`

const StyledButton = styled(Button)`
  ${Text('bodyText', 'alternate')};

  &:hover,
  &:focus {
    ${Text('bodyText', 'alternate')};
  }
`

export const ReadMore = withTheme(
  (props: ReadMoreProps & { theme: CombinedThemeInterface }) => {
    if (!props.content) {
      return null
    }
    const [open, setOpen] = React.useState(false)
    const contentRef = React.useRef<HTMLDivElement>(null)
    const [showReadMore, setShowReadMore] = React.useState(false)

    const bodyTextLineHeight =
      typeof props.theme.typography.bodyText.largeDevice.lineHeight === 'string'
        ? (props.theme.typography.bodyText.largeDevice.lineHeight as string)
        : ''

    const lineHeight = bodyTextLineHeight.includes('px')
      ? bodyTextLineHeight
      : DEFAULT_LINE_HEIGHT

    const closedMaxHeight =
      parseInt(lineHeight.replace('px', ''), 10) * NUM_OF_LINES_TO_SHOW

    React.useEffect(() => {
      if (contentRef && contentRef.current) {
        setShowReadMore(contentRef.current.scrollHeight > closedMaxHeight)
      }
    }, [])
    return (
      <Container>
        <Content
          ref={contentRef}
          content={props.content}
          open={open}
          showReadMore={showReadMore}
          maxHeight={
            open
              ? `${contentRef.current?.scrollHeight}px`
              : `${closedMaxHeight}px`
          }
        />
        {showReadMore && (
          <ButtonIconContainer
            open={open}
            lineHeight={lineHeight}
            data-testid="read-more-button"
            horizontalAlignment={props.horizontalAlignment}
          >
            <StyledButton
              emphasis="low"
              aria-expanded={open}
              onClick={() => setOpen(!open)}
            >
              {open ? props.readLessText : props.readMoreText}
            </StyledButton>
            <StyledChevronDown open={open} />
          </ButtonIconContainer>
        )}
      </Container>
    )
  },
)
